import {Injectable} from '@angular/core';
import {BehaviorSubject, ReplaySubject, Subject} from 'rxjs';
import {PlatformResponse} from 'src/app/interfaces/webservices-interface';
import {RedeemReward} from 'src/app/interfaces/reward-interface';
import {CustomerModel} from 'src/app/models/customer-model';
import {AuthenticationService} from '../authentication/authentication.service';
import {UserSessionService} from '../user-session/user-session.service';
import {WebservicesService} from '../webservices/webservices.service';
import { AppsflyerService } from '../marketing/appsflyer.service';

@Injectable({
  providedIn: 'root',
})
export class BoydRewardsService {
  public customer: CustomerModel;
  public boydCardIsConnectedSubject: Subject<boolean>;
  public hasRewardCard: BehaviorSubject<boolean> = new BehaviorSubject(false);

  constructor(
    public userSessionService: UserSessionService,
    private webservices: WebservicesService,
    private authenticationService: AuthenticationService,
    private appsFlyerService: AppsflyerService
  ) {
    this.boydCardIsConnectedSubject = new ReplaySubject(1);
    this.userSessionService.getCustomer().subscribe(customer => {
      this.customer = customer;
    });
  }

  callExternalConnect(data) {
    let trackingData = this.appsFlyerService.getTrackingIDs();
    if (trackingData?.deviceDetails) data.deviceDetails = trackingData.deviceDetails;
    if (trackingData?.externalSource) data.externalSource = trackingData.externalSource;

    return this.webservices.post(
      'authentication/external-system-connect',
      data
    );
  }

  connectBoydCardToStardust() {
    this.boydCardIsConnectedSubject.next(true);
    this.hasRewardCard.next(true);
  }
  
  disconnectBoydCardToStardust() {
    this.boydCardIsConnectedSubject.next(false);
    this.hasRewardCard.next(false);
  }

  checkConnectionToBoydCard() {
    this.authenticationService.callGetAccount().subscribe(res => {
      let connected = false;
      if ('external_associations' in res.payload) {
        const extAssociations: any[] = res.payload.external_associations;
        for (const e in extAssociations) {
          if (extAssociations[e].external_system === '12') {
            connected = true;
            break;
          }
        }
      } else {
        connected = false;
      }
      if (!connected) {
        this.disconnectBoydCardToStardust();
      } else {
        this.connectBoydCardToStardust();
      }
    });
    return this.boydCardIsConnectedSubject;
  }

  getCustomerLoyaltyPoints() {
    return this.webservices.post('');
  }

  // Get Redeem Rewards in respective to points and amounts
  getRedeemRewards(): Subject<PlatformResponse<RedeemReward[]>> {
    const obj = {
      session_token: this.userSessionService.getSessionToken(),
    };
    return this.webservices.get('rewards/get-redeem-rewards', obj);
  }

  // To enroll Customer for Boyd Rewards Program
  callEnrollPlayerRewards(obj): Subject<PlatformResponse<string>> {
    return this.webservices.post('rewards/enroll-player-rewards', obj);
  }

  // Get Boyd Points Info
  callGetExternalPointsInfo() {
    const obj = {
      customer_id: this.customer.customerId,
      session_token: this.userSessionService.getSessionToken(),
    };
    return this.webservices.get('rewards/get-external-points-info', obj);
  }

  // Convert Boyd Points to Dollars
  callRedeemRewards(data: {conversion: number; id: number}) {
    const obj = {
      conversion: data.conversion,
      customer_id: this.customer.customerId,
      session_token: this.userSessionService.getSessionToken(),
      redeemRewardId: data.id,
    };
    return this.webservices.get('rewards/convert-external-rewards-points', obj);
  }

  linkRewardCard(card: string) {
    const obj = {
      customer_id: this.customer.customerId,
      session_token: this.userSessionService.getSessionToken(),
      reward_number: card,
    };
    return this.webservices.post('rewards/link-reward-card', obj);
  }

  // Returns true if the player has a reward card.
  checkHasRewardCard() {
    const obj = {
      customer_id: this.customer.customerId,
      session_token: this.userSessionService.getSessionToken(),
    };
    return this.webservices.get('rewards/has-reward-card', obj);
  }

  setRewardCardStatus(rewardCardStatus) {
    this.hasRewardCard.next(rewardCardStatus);
  }

  getRewardCardStatus() {
    return this.hasRewardCard.asObservable();
  }
}
